// AI-CONTEXT: See docs/features/subscriptions-overview-page.md
import { useState } from 'react'
import PageHeader from '../../components/layout/PageHeader'
import Table from '../../components/common/table/Table.jsx'
import TableSearch from '../../components/common/table/TableSearch.jsx'
import useSubscriptionsOverview from '../../hooks/useSubscriptionsOverview'
import SubscriptionRow from '../../components/admin/SubscriptionRow'
import { STATUS_OPTIONS } from '../../helpers/subscriptionHelpers/subscriptionStatus'

const COLUMNS = [
    { key: 'name', label: 'Organisatie' },
    // Deze lijst en de volgorde van de <TableCell>'s in SubscriptionRow moeten
    // gelijk lopen; niets anders bewaakt dat. Zie de kolomtest in
    // SubscriptionsOverview.test.jsx.
    { key: 'status', label: 'Status' },
    { key: 'note', label: 'Notitie' },
    { key: 'modules', label: 'Modules' },
    { key: 'amount', label: 'Factuurbedrag en periodebegin' },
    { key: 'period', label: 'Loopt tot' },
    { key: 'billingType', label: 'Betaalmethode' },
    { key: 'monthly', label: 'Maandbedrag' },
]

/** Het tabblad dat niets wegfiltert. Geen stand, dus geen botsing met een status. */
const ALLE = 'alle'

/**
 * Abonnementenoverzicht van de Deccos-beheertenant: alle betalende organisaties,
 * met de nog in te vullen bedragen bovenaan.
 *
 * Drie toestanden, in deze volgorde: nog niet bekend, mislukt, en pas dan de
 * inhoud. De gedeelde tabel zegt bij een lege lijst "Geen resultaten gevonden",
 * en die bewering is onwaar zolang de gegevens onderweg zijn — vandaar dat de
 * tabel pas rendert als er iets te zeggen valt.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * Het zoeken gebeurt hier en NIET met `showSearch` van de gedeelde tabel. Die
 * filtert `items`, waardoor een rij die niet meer past uit beeld verdwijnt — en
 * met die rij het bedrag, de datum of de notitie die erin stond te wachten op
 * 'Opslaan'. Bij ~94 in te vullen rijen vul je er meerdere tegelijk, dus dat is
 * werk dat je kwijtraakt zonder waarschuwing. Alle rijen blijven daarom staan;
 * wat niet past wordt verborgen.
 * AI-CONTEXT: See docs/pitfalls.md#ui-filtering-a-list-unmounts-unsaved-input
 *
 * De tabbladen werken net zo: ze VERBERGEN de rijen die er niet in horen in
 * plaats van ze eruit te filteren, om dezelfde reden.
 *
 * De tellers op de tabbladen gaan over de héle lijst. Dat is de werkvoorraad —
 * zouden ze meebewegen met de zoekterm, dan leest "1 betaling inactief" als
 * "nog één te gaan".
 */
const SubscriptionsOverview = () => {
    const { rows, loading, error, replaceRow } = useSubscriptionsOverview()
    const [zoekterm, setZoekterm] = useState('')
    const [tab, setTab] = useState(ALLE)

    const term = zoekterm.trim().toLowerCase()
    const inTab = (row) => tab === ALLE || row.status === tab
    const matcht = (row) => inTab(row) && (!term || (row.name || '').toLowerCase().includes(term))
    const treffers = rows.filter(matcht).length

    // Over de hele lijst, niet over wat er nu zichtbaar is.
    const tabs = [
        { value: ALLE, label: 'Alle', aantal: rows.length },
        // Elke stand krijgt een tabblad, ook een lege: anders verdwijnt de helft
        // van de indeling zodra er even niemand in zit en is niet meer te zien
        // welke standen er bestaan.
        ...STATUS_OPTIONS.map(({ value, label }) => ({
            value,
            label,
            aantal: rows.filter((r) => r.status === value).length,
        })),
    ]

    return (
        <div className='subscriptions-overview'>
            <PageHeader heading='Abonnementen' />

            {loading && (
                <p className='subscriptions-overview-state'>Bezig met laden…</p>
            )}

            {!loading && error && (
                <p className='subscriptions-overview-state subscriptions-overview-state--error'>{error}</p>
            )}

            {!loading && !error && (
                <>
                    <p className='subscriptions-overview-summary'>
                        {`${rows.length} organisaties`}
                    </p>

                    <div className='subscriptions-tabs'>
                        {tabs.map(({ value, label, aantal }) => (
                            // Een echte <button> met aria-pressed, geen klikbare
                            // div: die is met het toetsenbord niet te bereiken,
                            // en de kleur alleen zegt een schermlezer niets.
                            <button
                                key={value}
                                type='button'
                                className={value === tab ? 'is-active' : ''}
                                aria-pressed={value === tab}
                                onClick={() => setTab(value)}
                            >
                                {`${label} (${aantal})`}
                            </button>
                        ))}
                    </div>

                    <TableSearch
                        value={zoekterm}
                        onChange={setZoekterm}
                        placeholder='Zoek op organisatie'
                    />

                    {/* De tabel toont bij nul treffers niets, want alle rijen
                        staan er nog — verborgen. Deze regels zeggen wat er aan
                        de hand is; zonder hen staat er een lege tabel zonder
                        uitleg. Eerst de zoekterm, want die is het laatste wat de
                        gebruiker deed. */}
                    {treffers === 0 && term && (
                        <p className='subscriptions-overview-state'>
                            {`Geen organisaties gevonden voor '${zoekterm.trim()}'.`}
                        </p>
                    )}
                    {treffers === 0 && !term && rows.length > 0 && (
                        <p className='subscriptions-overview-state'>
                            Geen organisaties met deze stand.
                        </p>
                    )}

                    <div className='table-container'>
                        <Table
                            columns={COLUMNS}
                            items={rows}
                            getItemKey={(row) => row.compagnyId}
                            showSearch={false}
                            emptyMessage='Geen organisaties gevonden.'
                            getRowClassName={(row) => `subscriptions-row--${row.status}`}
                            // `hidden` en niet een klasse met display:none: het
                            // is de standaardmanier om een rij weg te halen én
                            // hij is in tests zichtbaar als 'niet zichtbaar'.
                            rowProps={(row) => (matcht(row) ? {} : { hidden: true })}
                            renderRow={(row) => (
                                <SubscriptionRow row={row} onSaved={replaceRow} />
                            )}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default SubscriptionsOverview
